import { withZod } from '@remix-validated-form/with-zod'
import { WarningCircle } from 'iconoir-react'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewWorkspaceIntegrationModal } from '../AddNewWorkspaceIntegrationModal'
import Button from 'app/components/Button'
import Combo from 'app/components/ui/combo/combo'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormInput from 'app/components/form/FormInput'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import type { LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { fetchWorkspaceLocationHits, removeWorkspaceIntegration } from './pmsWizardHelpers'
import { useLocationStore } from 'app/stores/StoreContext'

// A remote entity (Ascend location or Carestack practice) the user maps to a workspace location.
export type PmsRemoteEntity = { id: string; name: string; isUsed: boolean }

// Result of a per-mapping save call.
export type PmsSaveResult = { success: boolean; error?: string }

// A step-1 credential field. The wizard renders one FormInput per field.
export type PmsCredentialField = { name: string; label: string }

// urql's CombinedError.message prefixes GraphQL errors with "[GraphQL] ". Strip it so the end user
// sees only the server message in the toast.
const cleanGqlMessage = (message: string | undefined): string | undefined => message?.replace(/^\[GraphQL\] /, ``)

// Everything that differs between PMS workspace wizards (Ascend, Carestack). The shared component
// owns the two-step flow; each PMS supplies its name, fields, GraphQL calls, and match logic.
export type PmsWizardConfig = {
  /** Display name used in all labels and messages, e.g. "Ascend" or "Carestack". */
  name: string
  /** REST setup endpoint path for the step-1 connect/configure form. */
  setupAction: string
  /** Step-1 credential fields. */
  fields: PmsCredentialField[]
  /** Zod schema for the step-1 fields. */
  schema: z.ZodType<Record<string, any>>
  /** Message shown when loading the remote entities fails. */
  loadErrorMessage: string
  /** Load the remote entities. Returns entities, or an error message when the call fails. */
  loadRemoteEntities: () => Promise<{ entities?: PmsRemoteEntity[]; errorMessage?: string }>
  /** Save one mapping pair (remote entity -> workspace location). */
  saveMapping: (remoteId: string, locationId: string) => Promise<PmsSaveResult>
  /** True when the workspace location is already linked to this remote entity. */
  isLocationLinked: (location: LocationType, remoteId: string) => boolean
  /** True when the workspace location is linked to any entity of this PMS. */
  isLocationUsed: (location: LocationType) => boolean
}

export type PmsWorkspaceWizardProps = {
  config: PmsWizardConfig
  onIntegrationChanged: () => void
  currentValues?: Record<string, unknown>
  /** Wizard back target: overrides returning to the workspace integration picker. */
  onBack?: () => void
}

export function PmsWorkspaceWizard({ config, onIntegrationChanged, currentValues, onBack }: PmsWorkspaceWizardProps) {
  const [confirm] = useConfirmation()
  const [step, setStep] = useState(1)
  const [remoteEntities, setRemoteEntities] = useState<PmsRemoteEntity[]>([])
  const [workspaceLocations, setWorkspaceLocations] = useState<LocationType[]>([])
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRemovable, setIsRemovable] = useState(false)
  const locationStore = useLocationStore()

  const validator = withZod(config.schema)

  const onMappingSelect = (remoteId: string, locationId: string) => {
    setMapping((prevMapping) => ({ ...prevMapping, [remoteId]: locationId }))
  }

  useEffect(() => {
    if (currentValues) {
      bg(async () => {
        const hits = await fetchWorkspaceLocationHits(locationStore)
        setIsRemovable(!hits.some((location) => config.isLocationUsed(location)))
      })
    }
  }, [currentValues])

  const onConnectCompleted = async (data: any) => {
    if (!data.success) {
      notifyError(`Failed to create integration: ${data.error}`)
      return
    }
    if (!currentValues) {
      notifySuccess(`${config.name} workspace integration created`)
      onIntegrationChanged()
    }
    setStep(2)
    setIsLoading(true)
    const { entities, errorMessage } = await config.loadRemoteEntities()
    if (entities) {
      setRemoteEntities(entities)
      const locationHits = await fetchWorkspaceLocationHits(locationStore)
      setWorkspaceLocations(locationHits)
      setMapping(() => {
        const newMapping: Record<string, string> = {}
        entities.forEach((entity) => {
          newMapping[entity.id] =
            locationHits.find((location) => config.isLocationLinked(location, entity.id))?.id ?? ``
        })
        return newMapping
      })
      setIsLoading(false)
    } else {
      setStep(1)
      notifyError(cleanGqlMessage(errorMessage) ?? config.loadErrorMessage)
      setIsLoading(false)
    }
  }

  const onSaveMappings = async () => {
    const pairs = Object.entries(mapping).filter(([, locationId]) => locationId)
    if (pairs.length === 0) {
      notifyError(`Map at least one ${config.name} location to a workspace location`)
      return
    }
    setIsLoading(true)
    for (const [remoteId, locationId] of pairs) {
      const saved = await config.saveMapping(remoteId, locationId)
      if (!saved.success) {
        setIsLoading(false)
        notifyError(`Failed to create location integrations: ${saved.error ?? `unknown error`}`)
        return
      }
    }
    setIsLoading(false)
    notifySuccess(`${config.name} locations connected successfully`)
    onIntegrationChanged()
    closeModal()
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewWorkspaceIntegrationModal onIntegrationChanged={onIntegrationChanged} />,
      {
        title: `Add New Workspace Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const goBackToStep1 = () => {
    setStep(1)
    setRemoteEntities([])
    setWorkspaceLocations([])
    setMapping({})
  }

  const onRemove = () => {
    return confirm({
      title: `Remove ${config.name} Workspace Integration?`,
      description: `Removing this workspace integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      removeWorkspaceIntegration({
        setupAction: config.setupAction,
        name: config.name,
        onIntegrationChanged,
        closeModal,
      }),
    )
  }

  const removeTooltip = `You need to first delete all ${config.name} location integrations to be able to remove this workspace integration.`

  const removeButton = currentValues ? (
    <Tooltip message={!isRemovable ? removeTooltip : undefined}>
      <Button label="Remove" mode="danger" size="lg" disabled={!isRemovable} onClick={() => onRemove()} />
    </Tooltip>
  ) : null

  // Both steps share the same sticky footer: Remove on the left, a Back-or-Cancel plus a primary
  // action on the right. `onLeft` is the Back target (picker for step 1, step 1 for step 2).
  const renderFooter = (onLeft: () => void, primary: React.ReactNode) => (
    <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
      <div className="flex justify-between align-middle">
        <div>{removeButton}</div>
        <div className="flex justify-end gap-x-4 align-middle">
          {!currentValues ? (
            <Button label="Back" mode="white" size="lg" onClick={onLeft} />
          ) : (
            <Button label="Cancel" mode="white" size="lg" onClick={() => closeModal()} />
          )}
          {primary}
        </div>
      </div>
    </div>
  )

  return (
    <>
      {step === 1 ? (
        <>
          <SmartForm
            id="integration-form"
            className="mt-0! w-150 grow-0 justify-start gap-y-4! p-4"
            validator={validator}
            reloadDocument={false}
            shouldPreventDefault={true}
            onPreventDefault={(data) => onConnectCompleted(data)}
            action={config.setupAction}
            hideButtons={true}
            method={currentValues ? `PUT` : `POST`}
            defaultValues={currentValues}
          >
            {!!currentValues && (
              <div className="col-span-6 ml-0! flex w-full items-center justify-between rounded bg-amber-100 p-3">
                <div className="flex items-center">
                  <div className="mr-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-200">
                    <WarningCircle className="h-5 w-5 text-yellow-800" />
                  </div>
                  <div className="text-body-sm text-default">
                    {config.name} configuration can't be modified. You need to first remove all {config.name} location
                    integrations, then remove the {config.name} workspace integration and start over.
                  </div>
                </div>
              </div>
            )}
            {config.fields.map((field) => (
              <FormInput
                key={field.name}
                name={field.name}
                cols={6}
                required
                label={field.label}
                labelClassName="text-body-sm text-strong"
                newLineError={false}
                disabled={!!currentValues}
                showErrorIcon
              />
            ))}
          </SmartForm>
          {renderFooter(
            () => (onBack ? onBack() : goBackToSelectIntegrationModal()),
            !currentValues ? (
              <Button type="submit" form="integration-form" label="Connect" mode="primary" size="lg" />
            ) : null,
          )}
        </>
      ) : (
        <>
          <div className="mt-0! flex max-h-[70vh] w-175 grow-0 flex-col justify-start gap-y-4! overflow-auto p-4 sm:grid sm:grid-cols-6">
            <div className="col-span-6 mt-6 text-ui-sm text-strong">
              Map {config.name} Locations to Workspace Locations
            </div>
            {isLoading ? (
              <Spinner size={6} />
            ) : remoteEntities.length === 0 ? (
              <div className="col-span-6 text-ui-sm text-default">No {config.name} locations found</div>
            ) : (
              remoteEntities.map((entity) => (
                <div className="col-span-6 grid grid-cols-6" key={entity.id}>
                  <div className="justify-left col-span-3 flex items-center text-body-xs leading-none text-strong">
                    {entity.name}
                  </div>
                  <Combo
                    className="col-span-3 w-80"
                    name={entity.id}
                    disabled={workspaceLocations.some((location) => config.isLocationLinked(location, entity.id))}
                    value={
                      Object.prototype.hasOwnProperty.call(mapping, entity.id)
                        ? {
                            id: mapping[entity.id],
                            value: mapping[entity.id],
                            name: workspaceLocations.find((location) => location.id === mapping[entity.id])?.name ?? ``,
                          }
                        : undefined
                    }
                    onChange={(option) => onMappingSelect(entity.id, option.value)}
                    options={workspaceLocations.map((location) => ({
                      value: location.id!,
                      name: location.name!,
                      id: location.id!,
                      meta: {
                        isUsed: config.isLocationUsed(location),
                        tooltipForDisabledOption: `This location is already connected to ${config.name}`,
                      },
                    }))}
                  />
                </div>
              ))
            )}
          </div>
          {renderFooter(
            () => goBackToStep1(),
            <Button label="Save" mode="primary" size="lg" loading={isLoading} onClick={() => bg(onSaveMappings())} />,
          )}
        </>
      )}
    </>
  )
}
